import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Mail,
  Send,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function ClaimDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/claims/${id}`);
      setData(r.data);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/claims/${id}/messages`, { body: reply });
      toast.success("Antwort gesendet");
      setReply("");
      reload();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/claims/${id}/status`, { status });
      toast.success("Status aktualisiert");
      reload();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Claim …
      </div>
    );
  }
  if (error || !data) return <div className="p-8 text-rose-600">{error || "Claim nicht gefunden."}</div>;

  const { claim, messages } = data;
  const isClosed = claim.status !== "open";

  return (
    <div data-testid="claim-detail-page">
      <PageHeader
        title={claim.subject}
        subtitle={`Dialog mit ${claim.recipient_email}`}
        actions={
          <>
            {claim.status === "open" ? (
              <Button
                variant="outline"
                onClick={() => setStatus("resolved")}
                className="rounded-sm text-emerald-700 hover:bg-emerald-50"
                data-testid="claim-resolve-btn"
              >
                <CheckCircle2 size={16} className="mr-2" /> Erledigt
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => setStatus("open")}
                className="rounded-sm"
                data-testid="claim-reopen-btn"
              >
                Wieder öffnen
              </Button>
            )}
            {claim.status === "open" && (
              <Button
                variant="outline"
                onClick={() => setStatus("closed")}
                className="rounded-sm text-slate-700"
                data-testid="claim-close-btn"
              >
                <XCircle size={16} className="mr-2" /> Schließen
              </Button>
            )}
          </>
        }
      >
        <div className="flex items-center gap-3 text-sm">
          <Link
            to="/claims"
            className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft size={14} /> Alle Claims
          </Link>
          <StatusBadge status={claim.status} />
          {claim.related_report_po && (
            <span className="font-mono text-slate-500">Report {claim.related_report_po}</span>
          )}
        </div>
      </PageHeader>

      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-8 space-y-4">
        <div className="bg-slate-50 border border-slate-200 rounded-md p-4 space-y-4">
          {messages.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-6">
              Keine Nachrichten im Dialog.
            </div>
          )}
          {messages.map((m) => (
            <MessageBubble key={m.id} message={m} />
          ))}
        </div>

        {isClosed ? (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-sm p-3">
            Der Dialog ist {claim.status === "resolved" ? "erledigt" : "geschlossen"}. Eingehende
            Antworten würden einen neuen Dialog eröffnen. Öffne den Claim wieder, um zu antworten.
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
            <Textarea
              rows={6}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Antwort schreiben …"
              data-testid="claim-reply-input"
            />
            <div className="flex justify-end">
              <Button
                onClick={sendReply}
                disabled={!reply.trim() || sending}
                className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                data-testid="claim-reply-send"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send size={14} className="mr-2" />
                )}
                Antwort senden
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MessageBubble({ message }) {
  const isOutgoing = message.direction === "outgoing";
  return (
    <div
      className={`flex ${isOutgoing ? "justify-end" : "justify-start"}`}
      data-testid={`claim-msg-${message.id}`}
    >
      <div
        className={`max-w-[80%] rounded-md px-4 py-3 ${
          isOutgoing
            ? "bg-slate-900 text-white"
            : "bg-white border border-slate-200 text-slate-900"
        }`}
      >
        <div
          className={`flex items-center justify-between gap-3 text-xs mb-2 ${
            isOutgoing ? "text-slate-300" : "text-slate-500"
          }`}
        >
          <span className="font-mono inline-flex items-center gap-1">
            <Mail size={11} /> {message.from_email}
          </span>
          <span>{formatDateTime(message.sent_at)}</span>
        </div>
        <pre className="whitespace-pre-wrap text-sm font-body leading-relaxed">{message.body}</pre>
      </div>
    </div>
  );
}
